import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM';

export class MarqueeSlider extends UcoastEl {
    static htmlSelector = 'marquee-slider'
    private textWrapper: HTMLElement
    private copyStarts: HTMLElement[]
    private speed: number
    private animation: number | null
    private currentPosition = 0
    private observer: IntersectionObserver
    private resizeObserver: ResizeObserver
    private motionPreference: MediaQueryList
    private loopWidth = 0
    private isIntersecting = false

    constructor() {
        super()
        this.textWrapper = q.rs('[data-uc-marquee-wrapper]', this)
        this.copyStarts = Array.from(
            q.ol('[data-uc-marquee-copy-start]', this) ?? []
        )
        this.speed = Number(this.dataset.speed) / 10 || 0.2
        this.animation = null
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)')

        this.handleMotionPreference = this.handleMotionPreference.bind(this)
        this.updateLoopWidth = this.updateLoopWidth.bind(this)

        this.resizeObserver = new ResizeObserver(this.updateLoopWidth)
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
            threshold: 0,
        })
    }

    override connectedCallback(): void {
        super.connectedCallback()
        this.motionPreference.addEventListener('change', this.handleMotionPreference)
        this.updateLoopWidth()
        this.resizeObserver.observe(this.textWrapper)
        this.observer.observe(this)
    }

    private startMarquee(): void {
        if (
            this.animation !== null ||
            this.motionPreference.matches ||
            !this.isIntersecting
        ) {
            return
        }

        this.animation = requestAnimationFrame(this.moveItems.bind(this))
    }

    private moveItems() {
        this.currentPosition -= this.speed

        if (this.loopWidth > 0 && -this.currentPosition >= this.loopWidth) {
            this.currentPosition += this.loopWidth
        } else if (
            this.loopWidth === 0 &&
            this.textWrapper.getBoundingClientRect().right < window.innerWidth
        ) {
            this.currentPosition = 0
        }

        this.applyPosition()
        this.animation = requestAnimationFrame(this.moveItems.bind(this))
    }

    stopMarquee(): void {
        if (this.animation !== null) {
            cancelAnimationFrame(this.animation)
            this.animation = null
        }

        this.currentPosition = 0
        this.applyPosition()
    }

    private applyPosition(): void {
        this.textWrapper.style.transform = `translateX(${this.currentPosition}px)`
    }

    private updateLoopWidth(): void {
        if (this.copyStarts.length < 2) return
        this.loopWidth =
            this.copyStarts[1].offsetLeft - this.copyStarts[0].offsetLeft
    }

    private handleMotionPreference(): void {
        if (this.motionPreference.matches) {
            this.stopMarquee()
        } else {
            this.startMarquee()
        }
    }

    private handleIntersection(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            this.isIntersecting = entry.isIntersecting
            if (entry.isIntersecting) {
                this.startMarquee()
            } else {
                this.stopMarquee()
            }
        })
    }

    override disconnectedCallback(): void {
        this.stopMarquee()
        this.observer.disconnect()
        this.resizeObserver.disconnect()
        this.motionPreference.removeEventListener('change', this.handleMotionPreference)
        super.disconnectedCallback()
    }
}
